import React, { useEffect, useState } from 'react';
import AmbassadorApplyForm from './AmbassadorApplyForm';
import { track } from '../services/analytics';
import './AmbassadorsPage.css';

/*
 * ─────────────────────────────────────────────────────────────────────────────
 * COPY REVIEW: everything in PERKS, LOOKING_FOR and FAQS below is a first
 * draft. The perks in particular describe commitments the program has to
 * actually honour — rewrite them before this page goes live.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const PERKS: { title: string; body: string }[] = [
  {
    title: 'Free Rally, always',
    body: 'Your own races are on us, plus credits to gift to friends and family so they can rally their people too.',
  },
  {
    title: 'A say in what we build',
    body: 'Early access to new features before anyone else, and a direct line to the two of us building Rally.',
  },
  {
    title: 'Rally gear',
    body: 'Ambassador kit for race day so people ask you about it at the start line.',
  },
];

const LOOKING_FOR: string[] = [
  'You’re running at least one race in the next 12 months',
  'You post about running somewhere — Instagram, TikTok, a group chat, a run club',
  'You already love Rally, or you’ve been looking for something exactly like it',
  'You’re happy to tell us when something about the app is broken or annoying',
];

const FAQS: { question: string; answer: string }[] = [
  {
    question: 'Do I need a big following?',
    answer:
      'No. Some of our best ambassadors have a few hundred followers and a very loud run club. We care far more about how you show up for other runners than about reach.',
  },
  {
    question: 'How much time does this take?',
    answer:
      'A few posts around the races you’re already running, plus the occasional check-in with us. No quotas, no content calendar, no scripts.',
  },
  {
    question: 'What do I actually have to do?',
    answer:
      'Use Rally on race day, share it with the runners around you, and tell us honestly what’s working and what isn’t. That’s it.',
  },
  {
    question: 'When will I hear back?',
    answer:
      'We review applications every week and email everyone either way — even if it’s a no for now.',
  },
  {
    question: 'I have a question that isn’t answered here.',
    answer:
      'Email us at hello@runwithrally.com or DM us on Instagram or TikTok. A real person replies.',
  },
];

// ── FAQ accordion ────────────────────────────────────────────────────────────

const AmbFAQItem: React.FC<{ question: string; answer: string }> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="amb-faq-item">
      <button
        className={`amb-faq-question${isOpen ? ' amb-faq-question--active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{question}</span>
        <span className="amb-faq-icon">{isOpen ? '−' : '+'}</span>
      </button>
      {isOpen && (
        <div className="amb-faq-answer">
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const AmbassadorsPage: React.FC = () => {
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    track('ambassador_page_viewed');
  }, []);

  // Lock background scroll while the questionnaire is open.
  useEffect(() => {
    document.body.style.overflow = showForm ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showForm]);

  const openForm = (source: string) => {
    track('ambassador_apply_started', { source });
    setShowForm(true);
  };

  return (
    <div className="amb-page">
      {/* Nav */}
      <div className="amb-nav">
        <div className="amb-nav-container">
          <a href="/" className="amb-nav-logo">
            <img src="/rally-font.svg" alt="Rally" className="amb-rally-logo" />
          </a>
          <div className="amb-nav-stores">
            <a
              href="https://apps.apple.com/us/app/rally-voice-notes-on-race-day/id6746744579"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/app-store-dark.svg" alt="Download on the App Store" className="amb-store-badge" />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.wimlabsllc.rallyapp"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/google-play-badge.png" alt="Get it on Google Play" className="amb-store-badge" />
            </a>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="amb-hero">
        <h1 className="amb-hero-title">Become a Rally Ambassador</h1>
        <p className="amb-hero-sub">
          We’re looking for runners who want to bring Rally to their race, their run club,
          and their people — so more runners hear the voices that keep them going.
        </p>
        <button className="amb-btn amb-btn--primary amb-btn--hero" onClick={() => openForm('hero')}>
          Apply to be an ambassador
        </button>
        <p className="amb-hero-note">Takes about two minutes</p>
      </section>

      <div className="amb-container">
        {/* Perks */}
        <section className="amb-perks-section">
          <h2 className="amb-section-title">What you get</h2>
          <div className="amb-perks-grid">
            {PERKS.map(perk => (
              <div className="amb-perk-card" key={perk.title}>
                <h3 className="amb-perk-title">{perk.title}</h3>
                <p className="amb-perk-body">{perk.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Who we're looking for */}
        <section className="amb-looking-section">
          <h2 className="amb-section-title">Who we’re looking for</h2>
          <ul className="amb-looking-list">
            {LOOKING_FOR.map(item => (
              <li className="amb-looking-item" key={item}>
                <span className="amb-check">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* FAQ */}
        <section className="amb-faq-section">
          <h2 className="amb-section-title">FAQ</h2>
          <div className="amb-faq-container">
            {FAQS.map(faq => (
              <AmbFAQItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </section>

        {/* Closing CTA */}
        <section className="amb-cta-section">
          <h2 className="amb-cta-title">Ready to rally?</h2>
          <p className="amb-cta-sub">
            Tell us about yourself and the races you’ve got coming up.
          </p>
          <button className="amb-btn amb-btn--primary" onClick={() => openForm('footer')}>
            Apply now
          </button>
        </section>
      </div>

      {showForm && <AmbassadorApplyForm onClose={() => setShowForm(false)} />}
    </div>
  );
};

export default AmbassadorsPage;
